import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2 } from 'lucide-react'
import { usersApi } from '@/lib/users/users.api'
import { authApi } from '@/lib/auth.api'
import { merkeFirma } from '@/lib/firma'
import { verlangtEinrichtung } from '@/lib/zwei-faktor'

type Einladung = { tenantId: string; tenantName: string; angenommen: boolean }

function meldungAus(err: unknown, ersatz: string): string {
  const m = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message
  return typeof m === 'string' && m.length < 200 ? m : ersatz
}

/**
 * AP-38-B — **Zugang zu einer weiteren Firma annehmen.**
 *
 * Der Link aus der Mail „Zugang zu … erhalten" führt hierher. Die Seite liegt
 * hinter der Anmeldung: Wer nicht angemeldet ist, landet auf der Anmeldeseite
 * und kommt danach mit dem Token zurück (`ProtectedRoute` → `state.from`).
 *
 * Sie nennt die Firma, bevor angenommen wird — eine Handlung, also ein Knopf mit
 * dem Verb (AP-36 Fall 3). Danach wechselt sie gleich in die neue Firma.
 */
export default function AcceptAccessPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [einladung, setEinladung] = useState<Einladung | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    if (!token) {
      setFehler(t('firma.einladungUngueltig'))
      return
    }
    usersApi
      .accessInvite(token)
      .then((res) => setEinladung(res.data.data))
      .catch((err) => setFehler(meldungAus(err, t('firma.einladungUngueltig'))))
  }, [token, t])

  async function annehmen() {
    if (!einladung) return
    setLaeuft(true)
    setFehler(null)
    try {
      const res = await usersApi.acceptAccess(token)
      await authApi.switchFirma(res.data.data.tenantId)
      merkeFirma(res.data.data.tenantId)
      window.location.assign('/')
    } catch (err) {
      // AP-38-E: Die Annahme eines externen Zugangs verlangt die Zwei-Faktor-Anmeldung —
      // der Wechsel in die neue Firma führt dann zuerst in die Einrichtung.
      if (verlangtEinrichtung(err)) return
      setLaeuft(false)
      setFehler(meldungAus(err, t('firma.einladungUngueltig')))
    }
  }

  return (
    <div className="mx-auto max-w-md p-6">
      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-foreground">
          <Building2 size={20} aria-hidden="true" />
          <h1 className="text-lg font-semibold">{t('firma.zugangTitel')}</h1>
        </div>

        {fehler && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {fehler}
          </p>
        )}

        {!fehler && !einladung && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

        {einladung && (
          <>
            <p className="mb-4 text-sm text-foreground">
              {einladung.angenommen
                ? t('firma.zugangSchonAngenommen', { firma: einladung.tenantName })
                : t('firma.zugangText', { firma: einladung.tenantName })}
            </p>
            {!einladung.angenommen && (
              <button
                type="button"
                onClick={annehmen}
                disabled={laeuft}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {laeuft ? t('common.loading') : t('firma.zugangAnnehmen')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
